import { ShieldCheck, ShieldAlert, KeyRound, Lock, Server } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Panel } from "@/components/common/Panel";
import { useAuth } from "@/context/AuthContext";
import { TeamAccessPanel } from "@/components/TeamAccessPanel";

function Row({ icon: Icon, title, detail, ok = true }) {
  return (
    <li className="flex items-start gap-3 px-3 py-4">
      <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${ok ? "bg-status-success-bg text-status-success" : "bg-status-warning-bg text-status-warning"}`}>
        <Icon className="h-4 w-4" />
      </span>
      <div>
        <p className="text-sm font-medium text-fundio-ink">{title}</p>
        <p className="mt-0.5 text-xs text-fundio-muted">{detail}</p>
      </div>
    </li>
  );
}

export default function SecurityStatus() {
  const { env } = useAuth();
  const isProd = !!env?.is_production;

  return (
    <div data-testid="security-status-page">
      <PageHeader title="Security Status" subtitle="Sicherheitsposture des FUNDIO Control OS (read-only Übersicht)." />

      <div data-testid="security-gap-note" className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-status-warning-border bg-status-warning-bg/60 px-4 py-2 text-xs text-status-warning">
        <span className="inline-flex items-center rounded-full border border-status-warning-border bg-status-warning-bg px-2.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wider">GAP</span>
        <span>Zeigt die real vorhandenen CONTROL-Sicherheitsmerkmale. Ein zentrales, kanonisches Security-Dashboard (Telemetrie / Alerts) ist noch nicht angebunden.</span>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="card-surface rounded-xl p-5"><div className="text-xs text-fundio-faint">Umgebung</div><div className={`mt-1.5 font-display text-lg font-medium ${isProd ? "text-status-success" : "text-status-warning"}`}>{env?.label || "Preview / Staging"}</div></div>
        <div className="card-surface rounded-xl p-5"><div className="text-xs text-fundio-faint">2FA Super Admin</div><div className="mt-1.5 font-display text-lg font-medium text-status-success">Pflicht (TOTP)</div></div>
        <div className="card-surface rounded-xl p-5"><div className="text-xs text-fundio-faint">Session-Widerruf</div><div className="mt-1.5 font-display text-lg font-medium text-status-success">Serverseitig aktiv</div></div>
      </div>

      <Panel title="Aktive Sicherheitsmassnahmen" testid="security-measures-panel" noPad>
        <ul className="divide-y divide-fundio-border">
          <Row icon={ShieldCheck} title="Mandatory TOTP-2FA für super_admin" detail="Login ohne 2FA ergibt keine Vollsession, sondern nur einen kurzlebigen Pre-Auth-Token (5 Min). Erst-Login erzwingt QR-Enrollment." />
          <Row icon={KeyRound} title="Serverseitige Session-Revocation (token_version)" detail="Logout und 2FA-Reset erhöhen token_version → alte Access- und Refresh-Tokens werden sofort ungültig (401)." />
          <Row icon={Lock} title="Cookie-only Sessions" detail="Keine Tokens im localStorage; httpOnly Secure Cookies. Der Pre-Auth-Token lebt nur im Frontend-Speicher." />
          <Row icon={Server} title="Support-/View-Sessions read-only & serverseitig gebunden" detail="Support-Sessions sind auf einen Tenant gebunden, mode=view, 15 Min TTL, jederzeit widerrufbar; Service-Tokens bleiben backend-only." />
          <Row icon={ShieldAlert} title="MFA-Recovery fail-closed" detail="MFA-Recovery-Proxy nur mit konfigurierter Ziel-E-Mail; ohne Konfiguration 503, kein Downstream-Call. Nonces/Secrets werden nie an den Browser geliefert." ok={false} />
        </ul>
      </Panel>

      <div className="mt-6"><TeamAccessPanel /></div>
    </div>
  );
}
